import React, {useContext} from 'react';
import {Box, Typography} from '@mui/material';
import {isNumber} from 'lodash-es';
import {themeColors} from '../../theme/Theme';
import {FormReadOnlyContext} from '../../components/AutoForm/AutoFormNew';

const scoreToBackground: Record<number, string> = {
  1: '#F0F8FF',
  2: '#E6F3FF',
  3: '#CCE7FF',
  4: '#B3D9FF',
  5: '#99CCFF',
};

const scoreToBorder: Record<number, string> = {
  1: '#E6F3FF',
  2: '#CCE7FF',
  3: '#B3D9FF',
  4: '#99CCFF',
  5: '#80BFFF',
};
// const scoreToBackground: Record<number, string> = {
//   1: '#d9c2ff',
//   2: '#c5c2ff',
//   3: '#BBDEFB',
//   4: '#B2DFDB',
//   5: '#C8E6C9',
// };

// const scoreToBorder: Record<number, string> = {
//   1: '#BCA9D9',
//   2: '#B2AFE6',
//   3: '#97B5D9',
//   4: '#91B5B2',
//   5: '#A3BBA4',
// };

export interface RiskSelectorProps {
  currentScore: number | null;
  readOnly?: boolean;
  disabled?: boolean;
  onSelect?: (score: number | null) => void;
  size?: 'small' | 'large';
  scoreTexts?: string[];
  error?: string;
}

export const PrintableRiskSelector: React.FC<RiskSelectorProps> = ({
  currentScore,
  readOnly: _readOnly = false,
  disabled,
  onSelect,
  size = 'small',
  scoreTexts,
  error,
}) => {
  const scoreText =
    currentScore && isNumber(currentScore) ? scoreTexts?.[currentScore - 1] : undefined;
  return (
    <Box display="flex" alignContent="center">
      <Typography>
        <strong>{currentScore || 'No score'} </strong>
      </Typography>
      <Typography style={{marginLeft: '16px'}}>{scoreText}</Typography>
    </Box>
  );
};

export const RiskSelector: React.FC<RiskSelectorProps> = ({
  currentScore,
  readOnly: _readOnly = false,
  disabled,
  onSelect,
  size = 'small',
  scoreTexts,
  error,
}) => {
  const {readOnly: readOnlyContext} = useContext(FormReadOnlyContext);
  const scores = [1, 2, 3, 4, 5];
  const readOnly = _readOnly || readOnlyContext;
  const boxHeight = size === 'small' ? '35px' : '45px';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          minWidth: 200,
          boxSizing: 'content-box',
          flex: 1,
          height: boxHeight,
          minHeight: boxHeight,
          alignContent: 'stretch',
          background: error ? themeColors.lightAlert : undefined,
          p: error ? 1 : 0,
          ml: error ? -1 : 0,
          mr: error ? -1 : 0,
          mt: error ? -1 : 0,
          mb: error ? -1 : 0,
          borderRadius: 4,
          // boxShadow: error ? '0px 0px 5px rgba(0, 0, 0, 0.2)' : undefined,
        }}
      >
        {scores.map((value, index) => {
          const selected = value === currentScore;
          const unselected = !selected && isNumber(currentScore);
          const scoreText = scoreTexts?.[index] || value.toString();
          const customLabel = scoreText !== value.toString();
          return (
            <Box
              key={value}
              sx={{
                flex: 1,
                display: 'flex',
                position: 'relative',
              }}
            >
              <Box
                data-selected={selected}
                sx={{
                  position: 'relative',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  borderRadius: 2,
                  border: selected ? `2px solid ${themeColors.coral}` : null,
                  backgroundColor: selected
                    ? themeColors.coral
                    : scoreToBackground[value],
                  color: selected ? themeColors.white : themeColors.black,
                  cursor: readOnly || disabled ? 'default' : 'pointer',
                  transform: selected ? 'scale(1.15)' : 'scale(1)',
                  transition: 'transform 0.1s ease-in-out',
                  fontWeight: !customLabel || selected ? 'bold' : 'normal',
                  fontSize: customLabel ? '0.8rem' : '1rem',
                  textAlign: 'center',
                  opacity: unselected || (disabled && !selected) ? 0.5 : 1,
                }}
                onClick={() => {
                  if (!_readOnly && !disabled && onSelect) {
                    if (selected) {
                      onSelect(null);
                    } else {
                      onSelect(value);
                    }
                  }
                }}
              >
                {scoreText}
              </Box>
            </Box>
          );
        })}
      </Box>
      {error ? (
        <Typography sx={{mb: 1, textAlign: 'center', mt: 1}} color="error">
          {error}
        </Typography>
      ) : null}
    </>
  );
};
